import React, { useState } from 'react';
import { Form, Input, Button, Typography, message, Card, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { UserOutlined, LockOutlined, SwapOutlined } from '@ant-design/icons';
import { useAuthStore } from '../store/authStore';
import { authAPI } from '../services/api';

const { Title, Text } = Typography;

interface SwitchUserFormValues {
  email: string;
  password: string;
}

const SwitchUserPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { login, logout, user: currentUser } = useAuthStore();

  const BrandIcon = () => (
    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
      <img 
        src="/logo.png" 
        alt="Logo" 
        style={{ 
          width: '200px', 
          height: '200px',
          objectFit: 'contain',
          display: 'block',
          margin: '0 auto'
        }} 
      />
    </div>
  );

  const onFinish = async (values: SwitchUserFormValues) => {
    setLoading(true);
    try {
      const response = await authAPI.login({
        email: values.email,
        password: values.password
      });
      
      if (response.data.token && response.data.user) {
        // Clear all previous state and login as new user
        logout(); // Clear previous session
        login(response.data.user, response.data.token);
        
        message.success(`Welcome, ${response.data.user.name || response.data.user.email}!`);
        
        // Navigate based on user role
        if (response.data.user.role === 'super_admin') {
          navigate('/client-selection');
        } else {
          navigate('/dashboard');
        }
      } else {
        message.error('Invalid response from server');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Login failed. Please try again.';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Go back to the previous page or dashboard
    navigate(-1);
  };

  return (
    <div className="auth-page">
      <Card className="auth-card">
        <div className="brand-header" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <BrandIcon />
        </div>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <SwapOutlined style={{ fontSize: '48px', color: '#607afb', marginBottom: '1rem' }} />
          <Title level={2} className="page-title">
            Switch User
          </Title>
          <Text style={{ color: '#47569e', display: 'block', marginBottom: '1rem' }}>
            Currently signed in as: <strong>{currentUser?.name || currentUser?.email}</strong>
          </Text>
          <Text style={{ color: '#8c8c8c' }}>
            Enter credentials to switch clients.
          </Text>
        </div>

        <Form
          form={form}
          name="switch-user"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          size="large"
        >
          <Form.Item
            label={<span className="form-item-label">Email</span>}
            name="email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="Enter email address"
              autoComplete="email"
            />
          </Form.Item>

          <Form.Item
            label={<span className="form-item-label">Password</span>}
            name="password"
            rules={[{ required: true, message: 'Please input your password!' }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Enter password"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{ width: '100%', height: '48px' }}
              >
                Switch User
              </Button>
              
              <Button
                type="default"
                onClick={handleCancel}
                size="large"
                style={{ width: '100%', height: '48px' }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default SwitchUserPage;
